import styled, { keyframes } from "styled-components";
import { colors } from "constants/colors";
import { StyledText } from "styles/Text";
import {
  FADE_DURATION_S,
  STEP_DURATION_MS,
} from "constants/pregnancyRevealReport";

// 진행 바 채우기
const fillProgress = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

// 위에서 아래로 내려오며 등장 (delay만큼 기다린 뒤 재생)
const fadeDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 별 딤 애니메이션
const scaleUp = keyframes`
  from {
    transform: scale(0.8);
  }
  to {
    transform: scale(1);
  }
`;

export const StyledContainer = styled.main<{
  $startColor: string;
  $endColor: string;
  $safeAreaBottom?: number;
}>`
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  background: linear-gradient(
    180deg,
    ${({ $startColor }) => $startColor} 0%,
    ${({ $endColor }) => $endColor} 100%
  );
  padding-bottom: ${({ $safeAreaBottom }) => $safeAreaBottom ?? 0}px;
  box-sizing: border-box;
`;

export const StyledInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const StyledHeaderCancelBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
`;

export const StyledContentBox = styled.div`
  position: relative;
  flex: 1;
  display: grid;
  max-height: calc(100% - 60px - 75px);
  overflow-x: hidden;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;
export const StyledContentInnerBox = styled.div`
  position: relative;
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
`;

export const StyledCustomHeader = styled.header<{
  $safeAreaTop?: number;
}>`
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: ${({ $safeAreaTop }) => $safeAreaTop ?? 0}px 16px 0px;
`;

export const StyledHeaderRow = styled.div`
  display: flex;
  height: 56px;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

export const StyledHeaderBox = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
`;

export const StyledStepBar = styled.div`
  z-index: 1;
  display: flex;
  height: 2px;
  gap: 4px;
  padding: 0 2px;
  align-items: center;
  flex-shrink: 0;
  max-width: 360px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;

export const StyledStepItem = styled.div`
  position: relative;
  flex: 1;
  height: 2px;
  border-radius: 100px;
  overflow: hidden;
`;

export const StyledStepItemBg = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${colors.white};
  opacity: 0.3;
`;

export const StyledStepItemFill = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${colors.white};
`;

export const StyledStepItemFillAnimating = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${colors.white};
  animation: ${fillProgress} ${STEP_DURATION_MS}ms linear forwards;
`;

export const StyledStepIntroBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 132px;
`;

export const StyledIntroAnimationBgBox = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

export const StyledIntroAnimationBgItem = styled.img<{
  $left: number;
  $top: number;
  $size: number;
  $index: number;
}>`
  position: absolute;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  animation: ${scaleUp} ${({ $index }) => 0.3 * ($index + 1)}s ease-in-out
    infinite alternate;
`;

export const StyledMainImageBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 56px;
  height: 56px;
  border-radius: 100%;
  background-color: rgba(255, 255, 255, 0.08);
  margin: auto auto 24px;
`;

export const StyledStepAnimationBox = styled.div<{
  $delay?: number;
  $marginBottom?: number;
}>`
  opacity: 0;
  animation: ${fadeDown} ${FADE_DURATION_S}s ease-out both;
  animation-delay: ${({ $delay }) => $delay || FADE_DURATION_S}s;
  margin-bottom: ${({ $marginBottom }) => $marginBottom || 0}px;
`;

export const StyledCardWithBorder = styled.div`
  display: flex;
  flex-direction: row;
  padding: 16px 16px 16px 12px;
  align-items: center;
  justify-content: space-between;
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 12px;
  margin: 0 20px;
`;

export const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin: 0 20px;
`;

export const StyledCardTitleInner = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px;
  padding-bottom: 8px;
  gap: 24px;
`;

export const StyledCardInner = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 12px 16px;
`;

export const StyledCardBottomInner = styled.div`
  box-sizing: border-box;
  padding: 16px;
  gap: 14px;
`;

export const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 100%;
`;

export const StyledJourneyContentLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const StyledJourneyContentTextBox = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  gap: 2px;
`;

export const StyledJourneyLineBox = styled.div<{ $isLast?: boolean }>`
  display: flex;
  flex-direction: column;
  padding-top: 20px;
  padding-left: 24px;
  padding-bottom: ${({ $isLast }) => ($isLast ? 20 : 0)}px;
  margin-left: 40px;
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  gap: 2px;
`;

export const StyledCardTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StyledGraphBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StyledTimelineBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 14px;
`;

export const StyledTimelineItemBox = styled.div<{ $opacity: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 16px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.1);
  opacity: ${({ $opacity }) => $opacity};
`;

export const StyledFetusCardInner = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px;
  gap: 20px;
`;

export const StyledFetusImg = styled.img`
  width: 100%;
  max-height: 160px;
  object-fit: contain;
  aspect-ratio: 1/1;
  margin: 0 auto;
`;

export const StyledCardWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: 12px;
  margin: 0 20px;
`;

export const StyledTextCard = styled.div`
  width: 100%;
  gap: 2px;
  box-sizing: border-box;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
`;

export const StyledRevealBox = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  margin: 0 40px;
`;

export const StyledCardButton = styled.button`
  display: flex;
  box-sizing: border-box;
  padding: 4px 20px 4px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 24px;
  border: 1px solid ${colors.lightGray1};
  margin: 20px auto 4px;
`;

export const StyledShareButtonBox = styled.div`
  z-index: 2;
  position: relative;
  margin-top: 20px;
  padding: 0 20px;
`;

export const StyledDiaryCardBox = styled.div`
  position: relative;
  z-index: 2;
  margin: 0 40px;
`;

export const StyledDiaryCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: 12px;
  box-sizing: border-box;
  padding: 20px 20px 24px;
  margin-top: 24px;
  gap: 24px;
  position: relative;
  z-index: 2;
  cursor: pointer;
`;

export const StyledProfileBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StyledProfileBabyBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

export const StyledProfileBabyImg = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 100%;
  border: 1px solid rgba(160, 160, 160, 0.2);
`;

export const StyledProfileBabyTextBox = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledBabyText = styled(StyledText.Body13)`
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

export const StyledDiaryCardBottomBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  margin-top: 55px;
  gap: 4px;
`;

export const StyledDiaryCardBottomTextBox = styled.div<{ $opacity: number }>`
  opacity: ${({ $opacity }) => $opacity};
`;

export const StyledProfileCtBox = styled.div`
  margin-top: 8px;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  grid-template-rows: repeat(3, auto);
  row-gap: 16px;
`;

export const StyledProfileCtWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

// 캡처 전용
export const StyledCaptureLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: -99;
`;

export const StyledStepFooter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 24px;
  padding-bottom: 24px;
`;

export const StyledCaptureLayerCover = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background-color: ${colors.white};
`;

export const StyledNav = styled.div`
  display: flex;
  z-index: 1;
  grid-column: 1;
  grid-row: 1;
`;

export const StyledNavHalf = styled.button`
  flex: 1;
  border: none;
  padding: 0;
  outline: none;
  user-select: none;
  cursor: pointer;
  background-color: transparent;
  touch-action: pan-y;

  /* ios 터치 하이라이트 제거 */
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;

  &:focus,
  &:active {
    background-color: transparent;
  }
`;
